import { AnalyticsSdk, generateUUID, getTimeZone, getSessionID, fetchLocusData, buildSearchContext } from '../pdtUtils';
import {
  LOB_RAILS,
  EVENT_NAME_PAGE_RENDERED,
  EVENT_TYPE_PAGE,
  STORE_ID_MMT,
  ORGANISATION_MMT,
  BU_GROUND_TRANSPORT,
  TRAFFIC_SOURCE_ORGANIC,
  PDT_SCHEMA_ID,
  PDT_SCHEMA_NAME
} from '../constants';

interface StationTrainData {
  trainNumber?: string;
  trainName?: string;
  sourceStn?: string;
  sourceStnName?: string;
  destinationStn?: string;
  destinationStnName?: string;
  departureTime?: string;
  arrivalTime?: string;
  duration?: string;
  trainWeeklySchedule?: Record<string, string>;
}

interface StationPageData {
  stationName?: string;
  stationCode?: string;
  railwayZone?: string;
  city?: string;
  passingTrainCount?: number;
  trainsStartingFromStation?: StationTrainData[];
  trainsPassingThroughStation?: StationTrainData[];
  trainsEndingAtStation?: StationTrainData[];
}

const DAYS_KEYS = ['runningSun', 'runningMon', 'runningTue', 'runningWed', 'runningThu', 'runningFri', 'runningSat'];
const DAYS_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseDurationToMinutes(duration?: string): number | null {
  if (!duration) return null;
  const hourMatch = duration.match(/(\d+)\s*hr/i);
  const minMatch = duration.match(/(\d+)\s*min/i);
  return (hourMatch ? parseInt(hourMatch[1], 10) : 0) * 60 + (minMatch ? parseInt(minMatch[1], 10) : 0);
}

function buildProductList(trains: StationTrainData[], offset: number) {
  return (
    trains?.map((train, index) => {
      const product: Record<string, any> = {
        id: train?.trainNumber || 'NA',
        train_number: parseInt(train?.trainNumber || '0', 10) || 0,
        train_name: train?.trainName || 'NA',
        duration: parseDurationToMinutes(train?.duration),
        position: { v: offset + index + 1 },
        from_station: {
          code: train?.sourceStn || 'NA',
          name: train?.sourceStnName || 'NA',
          departure_time: train?.departureTime || null
        },
        to_station: {
          code: train?.destinationStn || 'NA',
          name: train?.destinationStnName || 'NA',
          arrival_time: train?.arrivalTime || null
        },
        product_type: 'train'
      };
      if (train?.trainWeeklySchedule) {
        const schedule = train.trainWeeklySchedule;
        product.days_of_run = DAYS_KEYS.map((key, i) => ({
          day: DAYS_NAMES[i],
          running: schedule?.[key] === 'Y'
        }));
      }
      return product;
    }) || []
  );
}

export const logStationPageLoadEvent = async (stationData?: StationPageData) => {
  try {
    if (typeof window === 'undefined' || !AnalyticsSdk || !stationData) return;

    const isDev = process.env.NODE_ENV !== 'production';
    const sessionId = getSessionID();
    const journeyId = generateUUID();
    const locusData = await fetchLocusData(stationData?.stationCode);

    const searchContext = buildSearchContext({
      fromCode: stationData?.stationCode,
      fromName: stationData?.stationName,
      locusData
    });

    const starting = stationData?.trainsStartingFromStation || [];
    const passing = stationData?.trainsPassingThroughStation || [];
    const ending = stationData?.trainsEndingAtStation || [];
    const allProducts = [
      ...buildProductList(starting, 0),
      ...buildProductList(passing, starting.length),
      ...buildProductList(ending, starting.length + passing.length)
    ].slice(0, 50);

    const components = {
      id: 'station_page_load',
      component_type: 'station_detail',
      product_list: allProducts,
      content_details: [
        {
          id: 'station_info',
          type: 'station',
          position: { v: 1 },
          from: {
            location: {
              code: stationData?.stationCode || 'NA',
              name: stationData?.stationName || 'NA',
              country_code: 'IN',
              country: 'India'
            },
            locus: {
              locus_id: locusData?.fromLocusCode || 'NA',
              locus_type: locusData?.sourceLocusType || 'NA',
              country: 'India',
              locus_v2_id: locusData?.sourceLocusV2Id || 'NA',
              locus_v2_type: locusData?.sourceLocusV2Type || 'NA'
            }
          }
        }
      ]
    };

    const payload = {
      page_context: {
        lob: LOB_RAILS,
        page_type: 'template',
        page_name: 'rails_seo_station',
        prev_page_name: 'seo',
        funnel_step: 'detail',
        lob_category: LOB_RAILS,
        travel_store: { store_id: STORE_ID_MMT }
      },
      event_tracking_context: {
        env: isDev ? 'dev' : 'prod',
        timezone: getTimeZone(),
        session_id: sessionId,
        funnel_source: 'seo',
        bu: BU_GROUND_TRANSPORT,
        organisation: ORGANISATION_MMT,
        journey_id: journeyId,
        traffic_source: TRAFFIC_SOURCE_ORGANIC,
        template_id: PDT_SCHEMA_ID,
        topic_name: PDT_SCHEMA_NAME
      },
      search_context: searchContext,
      event_detail: {
        event_name: EVENT_NAME_PAGE_RENDERED,
        event_type: EVENT_TYPE_PAGE,
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_value: 'seo:rails:station:index',
        components
      },
      experiment_details: {
        valid_exp_list: [] as string[],
        variant_keys: '',
        honoured_exp_list: [] as string[]
      }
    };

    const xdmPayload = {
      ...payload,
      event_detail: {
        ...payload.event_detail,
        components: [components]
      }
    };

    const extraOptions = { lob: LOB_RAILS, destinationUrl: window?.location?.href || '' };

    AnalyticsSdk?.pushToPDT?.(payload, extraOptions);
    AnalyticsSdk?.pushToAdobeDataLayer?.(xdmPayload, extraOptions);
  } catch (e) {
    console.error('Analytics: station page load event failed', e);
  }
};
